/**
 * sovereignty.ts — where an organisation's data lives, as stated by its admin.
 *
 * Values come from `/sovereignty.json`, a static file the operator edits on the
 * web container (no rebuild needed). Anything left null is shown to users as
 * "not stated by your administrator"; the UI never guesses a location.
 *
 * Facts that follow from the code itself (TLS in transit, messages not
 * end-to-end encrypted, AI on the organisation's own Eurion servers) are fixed
 * in TrustPanel and do not belong in this file.
 */
import { useEffect, useState } from 'react';

export interface SovereigntyInfo {
  /** e.g. "Stockholm, Sweden" */
  dataLocation: string | null;
  /** Company that runs the servers, e.g. "Eurion" */
  operator: string | null;
  /** Free-text retention policy, e.g. "3 years, then deleted" */
  retention: string | null;
  /** Local model used for summaries and translation, e.g. "mistral-7b" */
  aiModel: string | null;
  /** Link to the organisation's privacy notice or DPA */
  privacyUrl: string | null;
}

export const EMPTY_SOVEREIGNTY: SovereigntyInfo = {
  dataLocation: null,
  operator: null,
  retention: null,
  aiModel: null,
  privacyUrl: null,
};

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** Accepts only known string fields, and only http(s) links. */
export function parseSovereignty(raw: unknown): SovereigntyInfo {
  if (!raw || typeof raw !== 'object') return EMPTY_SOVEREIGNTY;
  const r = raw as Record<string, unknown>;
  const url = str(r.privacyUrl);
  return {
    dataLocation: str(r.dataLocation),
    operator: str(r.operator),
    retention: str(r.retention),
    aiModel: str(r.aiModel),
    privacyUrl: url && /^https?:\/\//i.test(url) ? url : null,
  };
}

let cache: Promise<SovereigntyInfo> | null = null;

export function loadSovereignty(): Promise<SovereigntyInfo> {
  if (!cache) {
    cache = fetch('/sovereignty.json', { cache: 'no-cache' })
      .then(r => (r.ok ? r.json() : null))
      .then(parseSovereignty)
      .catch(() => EMPTY_SOVEREIGNTY);
  }
  return cache;
}

/** Test hook */
export function resetSovereigntyCache() {
  cache = null;
}

export function useSovereignty(): SovereigntyInfo {
  const [info, setInfo] = useState<SovereigntyInfo>(EMPTY_SOVEREIGNTY);
  useEffect(() => {
    let live = true;
    loadSovereignty().then(i => { if (live) setInfo(i); });
    return () => { live = false; };
  }, []);
  return info;
}
